/** CO / CE question bank: original items, validated by a trainer (draft → reviewed → published). */

import {
  type AcademyQuestionsListParams,
  type ContentStatusEnum,
  type LevelEnum,
  type Question,
  type QuestionSkillEnum,
  useAcademyQuestionsAudioCreate,
  useAcademyQuestionsCreate,
  useAcademyQuestionsList,
  useAcademyQuestionsPartialUpdate,
  useAcademyQuestionsPublishCreate,
  useAcademyQuestionsReviewCreate,
} from "@agencyos/api-client";
import { contentLanguage } from "@agencyos/core";
import { Badge, Button, GlassCard, GlassSheet, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { CheckCheck, Plus, Rocket, Upload } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { PicturesPreview } from "../../academy/ExamPicture";
import { fieldErrors } from "../../lib/settings";
import { tooSimilarMessage } from "./academyData";
import { CoAudioRowTools, CoAudioSummary } from "./CoAudioTools";
import { useAdminExam, useExamTexts } from "./examContext";
import { CONTENT_STATUSES, ErrorList, LEVELS, Pager, percent, StatusBadge, str, useAcademyRoles, useRefreshAcademy } from "./shared";

const SKILLS: QuestionSkillEnum[] = ["co", "ce"];

/** The API filters on these query parameters (not declared in the OpenAPI schema yet). */
type QuestionFilters = AcademyQuestionsListParams & { skill?: string; level?: string; status?: string };

export function OriginalContentBanner() {
  const { t } = useTranslation();
  return (
    <p role="note" className="notice-warning installment-row">
      {t("academyAdmin.originalRule")}
    </p>
  );
}

interface QuestionForm {
  skill: QuestionSkillEnum;
  level: LevelEnum;
  kind: string;
  theme: string;
  document: string;
  stem: string;
  options: [string, string, string, string];
  answer: number;
  explanation: string;
}

function toForm(q: Question | undefined): QuestionForm {
  const options = Array.isArray(q?.options) ? (q.options as unknown[]).map(str) : [];
  return {
    skill: q?.skill ?? "ce",
    level: q?.level ?? "A1",
    kind: q?.kind ?? "",
    theme: q?.theme ?? "",
    document: q?.document ?? "",
    stem: q?.stem ?? "",
    options: [options[0] ?? "", options[1] ?? "", options[2] ?? "", options[3] ?? ""],
    answer: q?.answer ?? 0,
    explanation: q?.explanation ?? "",
  };
}

/** The anti-plagiarism refusal (400, code « too_similar », « trop proche d'une source protégée »). */
export function isTooClose(error: unknown): boolean {
  return tooSimilarMessage(error) !== null;
}

/** The refusal with the server's message (which source, how close), shown where publish is called. */
export function TooSimilarAlert({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const message = tooSimilarMessage(error);
  if (message === null) return null;
  return (
    <div role="alert" className="notice-warning installment-row stack">
      <strong>{t("academyAdmin.tooClose")}</strong>
      {message ? <span>{message}</span> : null}
    </div>
  );
}

function QuestionSheet({ question, onClose }: { question: Question | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const create = useAcademyQuestionsCreate({ mutation: { onSuccess: refresh } });
  const update = useAcademyQuestionsPartialUpdate({ mutation: { onSuccess: refresh } });
  const review = useAcademyQuestionsReviewCreate({ mutation: { onSuccess: refresh } });
  const publish = useAcademyQuestionsPublishCreate({ mutation: { onSuccess: refresh } });
  const audio = useAcademyQuestionsAudioCreate({ mutation: { onSuccess: refresh } });
  const [current, setCurrent] = useState<Question | undefined>(question);
  const [form, setForm] = useState<QuestionForm>(() => toForm(question));
  const [file, setFile] = useState<File | null>(null);
  const set = <K extends keyof QuestionForm>(key: K, value: QuestionForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const saveError = current ? update.error : create.error;
  const errors = fieldErrors(saveError);
  const pending = create.isPending || update.isPending || review.isPending || publish.isPending || audio.isPending;
  const status: ContentStatusEnum = current?.status ?? "draft";

  const payload = () => ({
    skill: form.skill,
    level: form.level,
    kind: form.kind.trim(),
    theme: form.theme.trim(),
    document: form.document,
    stem: form.stem.trim(),
    options: form.options.map((o) => o.trim()),
    answer: form.answer,
    explanation: form.explanation,
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    review.reset();
    publish.reset();
    if (current) update.mutate({ id: current.id, data: payload() }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
    else create.mutate({ data: payload() }, { onSuccess: (r) => r.status === 201 && setCurrent(r.data) });
  };
  const onUpload = () => {
    if (!current || !file) return;
    audio.mutate(
      { id: current.id, data: { audio: file } },
      {
        onSuccess: (r) => {
          if (r.status !== 200) return;
          setCurrent(r.data);
          setFile(null);
        },
      },
    );
  };
  const onReview = () => {
    if (!current) return;
    publish.reset();
    review.mutate({ id: current.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
  };
  const onPublish = () => {
    if (!current) return;
    review.reset();
    publish.mutate({ id: current.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
  };

  return (
    <GlassSheet open title={current ? t("academyAdmin.questions.edit") : t("academyAdmin.questions.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <OriginalContentBanner />
        {current ? (
          <div className="row">
            <StatusBadge status={status} />
            <span className="muted">{t("academyAdmin.similarity", { value: percent(current.similarity) })}</span>
          </div>
        ) : null}
        <fieldset className="plain-fieldset stack" disabled={!canEdit}>
          <div className="form-grid">
            <SelectField
              label={t("academyAdmin.skillLabel")}
              value={form.skill}
              options={SKILLS.map((s) => ({ value: s, label: t(`academyAdmin.skill.${s}`) }))}
              onChange={(e) => set("skill", e.target.value as QuestionSkillEnum)}
            />
            <SelectField
              label={t("academyAdmin.level")}
              value={form.level}
              error={errors.level}
              options={LEVELS.map((l) => ({ value: l, label: l }))}
              onChange={(e) => set("level", e.target.value as LevelEnum)}
            />
            <TextField
              label={t("academyAdmin.questions.kind")}
              hint={t("academyAdmin.questions.kindHint")}
              value={form.kind}
              maxLength={60}
              error={errors.kind}
              onChange={(e) => set("kind", e.target.value)}
            />
            <TextField label={t("academyAdmin.theme")} value={form.theme} maxLength={80} error={errors.theme} onChange={(e) => set("theme", e.target.value)} />
          </div>
          <TextAreaField
            label={form.skill === "co" ? t("academyAdmin.questions.script") : t("academyAdmin.questions.text")}
            hint={form.skill === "co" ? t("academyAdmin.questions.scriptHint") : undefined}
            lang="fr"
            rows={8}
            value={form.document}
            error={errors.document}
            onChange={(e) => set("document", e.target.value)}
          />
          <TextField
            label={t("academyAdmin.questions.stem")}
            lang="fr"
            value={form.stem}
            maxLength={500}
            error={errors.stem}
            onChange={(e) => set("stem", e.target.value)}
          />
          <PicturesPreview pictures={current?.pictures} options={form.options} />
          <fieldset className="plain-fieldset stack">
            <legend>
              <strong>{t("academyAdmin.questions.options")}</strong>
            </legend>
            <p className="muted">{t("academyAdmin.questions.optionsHint")}</p>
            {form.options.map((option, n) => (
              <div key={n} className="row academy-option">
                <label className="academy-option__radio">
                  <input
                    type="radio"
                    name="answer"
                    checked={form.answer === n}
                    onChange={() => set("answer", n)}
                    aria-label={t("academyAdmin.questions.rightAnswer", { letter: "ABCD"[n] })}
                  />
                  <span aria-hidden="true">{"ABCD"[n]}</span>
                </label>
                <TextField
                  className="academy-option__field"
                  label={t("academyAdmin.questions.option", { letter: "ABCD"[n] })}
                  lang="fr"
                  value={option}
                  onChange={(e) =>
                    set(
                      "options",
                      form.options.map((o, i) => (i === n ? e.target.value : o)) as QuestionForm["options"],
                    )
                  }
                />
              </div>
            ))}
            {errors.options || errors.answer ? (
              <p role="alert" className="ao-field__error">
                {errors.options ?? errors.answer}
              </p>
            ) : null}
          </fieldset>
          <TextAreaField
            label={t("academyAdmin.questions.explanation")}
            rows={3}
            value={form.explanation}
            error={errors.explanation}
            onChange={(e) => set("explanation", e.target.value)}
          />
          {saveError && !Object.keys(errors).some((k) => k !== "detail" && k !== "non_field_errors") ? <ErrorList error={saveError} /> : null}
          <div className="row">
            <Button type="submit" disabled={pending}>
              {pending ? t("common.loading") : current ? t("common.save") : t("academyAdmin.create")}
            </Button>
            {update.isSuccess || create.isSuccess ? (
              <span role="status" className="ok">
                {current && status === "draft" ? t("academyAdmin.savedAsDraft") : t("common.saved")}
              </span>
            ) : null}
          </div>
          {current ? <p className="muted">{t("academyAdmin.editBackToDraft")}</p> : null}
        </fieldset>
      </form>

      {current && form.skill === "co" ? (
        <section className="stack">
          <h3>{t("academyAdmin.questions.audio")}</h3>
          {current.audio_url ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- its script is the document field above
            <audio controls preload="none" src={current.audio_url} aria-label={t("academyAdmin.questions.audio")} />
          ) : (
            <p className="muted">{t("academyAdmin.questions.noAudio")}</p>
          )}
          {canEdit ? (
            <div className="row">
              <label className="ao-field">
                <span className="ao-field__label">{t("academyAdmin.questions.audioFile")}</span>
                <input type="file" accept="audio/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
              <Button variant="secondary" icon={<Upload size={16} aria-hidden="true" />} disabled={!file || pending} onClick={onUpload}>
                {t("academyAdmin.questions.upload")}
              </Button>
            </div>
          ) : null}
          <ErrorList error={audio.error} />
        </section>
      ) : null}

      {current && canEdit ? (
        <section className="stack">
          <h3>{t("academyAdmin.validation")}</h3>
          <p className="muted">{t("academyAdmin.validationHint")}</p>
          <div className="row">
            <Button variant="secondary" icon={<CheckCheck size={16} aria-hidden="true" />} disabled={pending || status !== "draft"} onClick={onReview}>
              {t("academyAdmin.review")}
            </Button>
            <Button icon={<Rocket size={16} aria-hidden="true" />} disabled={pending || status === "published"} onClick={onPublish}>
              {t("academyAdmin.publish")}
            </Button>
          </div>
          <TooSimilarAlert error={publish.error ?? review.error} />
          <ErrorList error={review.error ?? (isTooClose(publish.error) ? null : publish.error)} />
          {review.isSuccess || publish.isSuccess ? (
            <span role="status" className="ok">
              {t(`academyAdmin.status.${status}`)}
            </span>
          ) : null}
        </section>
      ) : null}
    </GlassSheet>
  );
}

export function QuestionsTab() {
  const { t } = useTranslation();
  const { canEdit: canEditRole } = useAcademyRoles();
  const { exam, ielts, passages, contentParams: params } = useAdminExam();
  // IELTS questions belong to a passage (Passages tab); this form writes TCF multiple choice only.
  // D-123: the TEF and German items come from their loaders too (this form has no exam: it
  // created a TCF item while the TEF or German list was shown)
  const canEdit = canEditRole && !passages;
  const texts = useExamTexts();
  const skillLabel = texts.skill; // D-123: Hören… for German
  const [skill, setSkill] = useState("");
  const [level, setLevel] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Question | "new" | null>(null);
  const close = useCallback(() => setEditing(null), []);
  const filters: QuestionFilters = { page, ...params, skill: skill || undefined, level: level || undefined, status: status || undefined };
  const list = useAcademyQuestionsList(filters);
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const filter = (apply: (value: string) => void) => (value: string) => {
    apply(value);
    setPage(1);
  };

  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{ielts ? t("academyAdmin.ielts.questions.title") : texts.title("questions", "academyAdmin.questions.title")}</h2>
          <p className="muted">{ielts ? t("academyAdmin.ielts.questions.intro") : t("academyAdmin.questions.intro")}</p>
        </div>
        {canEdit ? (
          <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setEditing("new")}>
            {t("academyAdmin.questions.new")}
          </Button>
        ) : null}
      </div>
      <OriginalContentBanner />
      {passages ? (
        <p role="note" className="muted">
          {t("academyAdmin.ielts.questions.passageNote")}
        </p>
      ) : (
        <CoAudioSummary />
      )}
      <div className="form-grid">
        <SelectField
          label={t("academyAdmin.skillLabel")}
          value={skill}
          options={[{ value: "", label: t("academyAdmin.all") }, ...SKILLS.map((s) => ({ value: s, label: skillLabel(s) }))]}
          onChange={(e) => filter(setSkill)(e.target.value)}
        />
        <SelectField
          label={t("academyAdmin.level")}
          value={level}
          options={[{ value: "", label: t("academyAdmin.all") }, ...LEVELS.map((l) => ({ value: l, label: l }))]}
          onChange={(e) => filter(setLevel)(e.target.value)}
        />
        <SelectField
          label={t("academyAdmin.statusLabel")}
          value={status}
          options={[{ value: "", label: t("academyAdmin.all") }, ...CONTENT_STATUSES.map((s) => ({ value: s, label: t(`academyAdmin.status.${s}`) }))]}
          onChange={(e) => filter(setStatus)(e.target.value)}
        />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.questions.stem")}</th>
                  <th>{t("academyAdmin.skillLabel")}</th>
                  <th>{t("academyAdmin.level")}</th>
                  <th>{t("academyAdmin.theme")}</th>
                  <th>{t("academyAdmin.questions.successRate")}</th>
                  <th>{t("academyAdmin.similarityShort")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((q) => (
                  <tr key={q.id}>
                    <td>
                      {passages ? (
                        <span className="stack academy-kind">
                          <span lang={contentLanguage(exam)}>{q.stem}</span>
                          <span className="row">
                            <Badge tone="info">{t(`academyAdmin.ielts.exercise.type.${q.question_type ?? "mcq"}`)}</Badge>
                            {q.number ? <span className="muted ao-num">{t("academyAdmin.ielts.questions.number", { n: q.number })}</span> : null}
                          </span>
                        </span>
                      ) : (
                        <button type="button" className="link" lang="fr" onClick={() => setEditing(q)}>
                          {q.stem}
                        </button>
                      )}
                      {!passages && q.skill === "co" && !q.audio_url ? (
                        <>
                          {" "}
                          <Badge tone="danger">{t("academyAdmin.questions.noAudioShort")}</Badge>
                        </>
                      ) : null}
                      {passages ? null : <CoAudioRowTools question={q} />}
                    </td>
                    <td>{ielts ? t(`academyAdmin.ielts.skillShort.${q.skill}`) : q.skill.toUpperCase()}</td>
                    <td>{q.level}</td>
                    <td>{q.theme || "—"}</td>
                    <td className="nowrap">
                      {percent(q.success_rate)}
                      <span className="muted"> · {t("academyAdmin.questions.answered", { count: q.times_answered })}</span>
                    </td>
                    <td>{percent(q.similarity)}</td>
                    <td>
                      <StatusBadge status={q.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <p className="muted">{t("academyAdmin.questions.empty")}</p>
      )}
      {editing ? <QuestionSheet key={editing === "new" ? "new" : editing.id} question={editing === "new" ? undefined : editing} onClose={close} /> : null}
    </GlassCard>
  );
}
